import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPasswordPage() {
  const { forgotPassword, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="Reset link sent">
        <div className="flex flex-col items-center text-center gap-4 py-2" data-testid="forgot-success">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <MailCheck className="h-7 w-7 text-emerald-400" />
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            If an account exists for <span className="text-slate-100 font-medium">{email}</span>, we've sent a secure
            link to reset your password. The link expires in 1 hour.
          </p>
          <Link data-testid="back-to-login" to="/login" className="text-sm text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-1.5 mt-2">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot password" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
          <Input
            id="email" type="email" autoComplete="email" required
            data-testid="forgot-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-slate-950 border-slate-800 text-slate-100 h-11"
            placeholder="you@example.com"
          />
        </div>
        {error && (
          <div data-testid="forgot-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
          className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
      <div className="mt-6 pt-5 border-t border-slate-800 text-sm text-slate-400 text-center">
        <Link data-testid="back-to-login" to="/login" className="text-amber-400 hover:text-amber-300 font-semibold inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
